'use client';

import { Button, Label, Modal, ModalBody, ModalHeader, Select, TextInput } from 'flowbite-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { toastError } from '../../components/Toast.tsx';
import { useCreateCompanyMutation, useGetAllBuildingsQuery } from '../../lib/model/kantoordagApi.ts';

// Bedrijf.EXTERNAL_COMPANY.name (BedrijfResource.kt) -- never a real, creatable company (FR-007).
// Checked client-side too so the reserved-name case gets an immediate, specific field error instead
// of waiting on a 409 round-trip that (per the actual WebApplicationException(message, status)
// implementation) carries no response body to distinguish it from a duplicate name/domain anyway.
const RESERVED_COMPANY_NAME = 'Extern';

interface CreateCompanyDialogProps {
    show: boolean;
    onClose: () => void;
    onCreated: (companyName: string) => void;
}

export default function CreateCompanyDialog({ show, onClose, onCreated }: CreateCompanyDialogProps) {
    const { t } = useTranslation('company-admin');
    const { data: buildings } = useGetAllBuildingsQuery();
    const [createCompany, createCompanyResult] = useCreateCompanyMutation();

    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [logo, setLogo] = useState('');
    const [building, setBuilding] = useState('');

    // 409s from POST /bedrijf/{name} cover three distinct conflicts (duplicate name, duplicate
    // domain, the reserved sentinel) but the backend response carries no body to tell them apart
    // (WebApplicationException(message, status) never sets an entity) -- so a 409 is surfaced next
    // to both the name and domain fields rather than guessing which one caused it.
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [domainError, setDomainError] = useState<string | undefined>(undefined);
    const [buildingError, setBuildingError] = useState<string | undefined>(undefined);

    function close() {
        setName('');
        setDomain('');
        setLogo('');
        setBuilding('');
        setNameError(undefined);
        setDomainError(undefined);
        setBuildingError(undefined);
        onClose();
    }

    function submit() {
        setNameError(undefined);
        setDomainError(undefined);
        setBuildingError(undefined);

        const trimmedName = name.trim();
        const trimmedDomain = domain.trim();

        let hasError = false;
        if (trimmedName === '') {
            setNameError(t('company_name_required'));
            hasError = true;
        } else if (trimmedName === RESERVED_COMPANY_NAME) {
            setNameError(t('company_name_reserved'));
            hasError = true;
        }
        if (trimmedDomain === '') {
            setDomainError(t('company_domain_required'));
            hasError = true;
        }
        if (building === '') {
            setBuildingError(t('company_building_required'));
            hasError = true;
        }
        if (hasError) {
            return;
        }

        createCompany({
            name: trimmedName,
            companyWrite: {
                domain: trimmedDomain,
                logo: logo.trim(),
                building,
            },
        })
            .unwrap()
            .then(() => {
                toast.success(t('company_created'));
                onCreated(trimmedName);
                close();
            })
            .catch((error) => {
                if (error?.status === 409) {
                    setNameError(t('company_create_conflict'));
                    setDomainError(t('company_create_conflict'));
                } else if (error?.status === 404) {
                    setBuildingError(t('company_building_not_found'));
                } else {
                    toastError({ api: createCompanyResult, message: t('company_create_failed') });
                }
            });
    }

    return (
        <Modal show={show} size="md" onClose={close} popup>
            <ModalHeader>{t('create_company')}</ModalHeader>
            <ModalBody>
                <div data-testid="create-company-dialog" className="flex flex-col gap-4">
                    <div>
                        <Label htmlFor="new-company-name">{t('new_company_name')}</Label>
                        <TextInput
                            id="new-company-name"
                            value={name}
                            color={nameError != null ? 'failure' : undefined}
                            onChange={event => setName(event.target.value)}
                        />
                        {nameError != null && <p className="mt-1 text-sm text-red-600">{nameError}</p>}
                    </div>
                    <div>
                        <Label htmlFor="new-company-domain">{t('new_company_domain')}</Label>
                        <TextInput
                            id="new-company-domain"
                            value={domain}
                            color={domainError != null ? 'failure' : undefined}
                            onChange={event => setDomain(event.target.value)}
                        />
                        {domainError != null && <p className="mt-1 text-sm text-red-600">{domainError}</p>}
                    </div>
                    <div>
                        <Label htmlFor="new-company-logo">{t('new_company_logo')}</Label>
                        <TextInput
                            id="new-company-logo"
                            value={logo}
                            onChange={event => setLogo(event.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="new-company-building">{t('new_company_building')}</Label>
                        <Select
                            id="new-company-building"
                            value={building}
                            color={buildingError != null ? 'failure' : undefined}
                            onChange={event => setBuilding(event.target.value)}
                        >
                            <option value="">{t('select_building')}</option>
                            {buildings?.map(b => (
                                <option key={b.name} value={b.name}>{b.name}</option>
                            ))}
                        </Select>
                        {buildingError != null && <p className="mt-1 text-sm text-red-600">{buildingError}</p>}
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button color="secondary" onClick={close}>{t('cancel')}</Button>
                        <Button onClick={submit} disabled={createCompanyResult.isLoading}>{t('create_company')}</Button>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}
